import { getHaversineMileage } from 'utils/helpers';

/**
 * Fixed buffer multiplier to account for the deviation of real-world routes from approximate straight-line distances.
 */
const HAVERSINE_BUFFER_MULTIPLIER = 1.1;

/**
 * Derived from linear regression of internal trip data comparing actual mileage to Haversine-estimated mileage to compare the deviation of real-world mileages with approximate straight-line distances.
 */
const HAVERSINE_CORRECTION_CONSTANT = 0.00088218;

/**
 * Maximum effective multiplier cap. For long-distance trips (150+ miles), routes are predominantly
 * highway and the road-to-straight-line ratio stabilizes around 1.20. Without this cap, the quadratic
 * correction over-estimates because the regression was trained primarily on short trips (75%+ under 32 miles).
 * Validated against historical data: capping at 1.20 reduces mean absolute error from 24→16 mi (200-250 bucket)
 * and 35→21 mi (250-300 bucket).
 */
const MAX_EFFECTIVE_MULTIPLIER = 1.2;

/**
 * Calculates the estimated mileage between an origin and destination using the Haversine formula and adjusts it based on internal data.
 *
 * Mileage calculation formula:
 *
 *  `adjusted mileage estimate = H × min(HAVERSINE_BUFFER_MULTIPLIER + HAVERSINE_CORRECTION_CONSTANT × H, MAX_EFFECTIVE_MULTIPLIER)`
 *
 * where:
 *  - `H` is the Haversine distance between the origin and destination.
 *  - `HAVERSINE_BUFFER_MULTIPLIER` (1.10) is the baseline buffer percentage for short trips.
 *  - `HAVERSINE_CORRECTION_CONSTANT` (0.00088218) is the slope that increases the buffer for medium-distance trips.
 *  - `MAX_EFFECTIVE_MULTIPLIER` (1.20) caps the total multiplier for long-distance trips (150+ miles) where routes are predominantly highway and the road-to-straight-line ratio stabilizes.
 *
 * For trips under ~113 Haversine miles, the uncapped formula applies. Beyond that threshold,
 * the multiplier is capped at 1.20 to prevent over-estimation on long highway trips.
 *
 * @param {Array<number>} originLongLat - Array containing `[longitude, latitude]` coordinates of the origin.
 * @param {Array<number>} destinationLongLat - Array containing `[longitude, latitude]` coordinates of the destination.
 * @returns {number | null} The estimated mileage between the origin and destination. Returns `null` if the origin or destination is not a valid array of long-lat coordinates.
 */
const getEstimatedMileage = (originLongLat, destinationLongLat) => {
  // Validate origin and destination long-lat arrays
  if (
    !Array.isArray(originLongLat) ||
    originLongLat?.length !== 2 ||
    originLongLat?.some((coord) => !Number.isFinite(coord)) ||
    !Array.isArray(destinationLongLat) ||
    destinationLongLat?.length !== 2 ||
    destinationLongLat?.some((coord) => !Number.isFinite(coord))
  )
    return null;

  const haversineMileage = getHaversineMileage(
    originLongLat,
    destinationLongLat,
  );

  // Adjusted Haversine mileage formula with cap for long-distance trips
  const effectiveMultiplier = Math.min(
    HAVERSINE_BUFFER_MULTIPLIER +
      HAVERSINE_CORRECTION_CONSTANT * haversineMileage,
    MAX_EFFECTIVE_MULTIPLIER,
  );

  return haversineMileage * effectiveMultiplier;
};

export default getEstimatedMileage;
